import { Injectable } from '@angular/core';

/** Which of the three teacher-tools surfaces is currently shown — a user may hold more than one role. */
export type TeacherToolsViewMode = 'ADMIN' | 'HOD' | 'TEACHER';

/**
 * Remembers the surface a user switched to.
 *
 * The overview is recreated on every navigation back to teacher tools and picks its view from
 * the caller's roles — right on a first visit, wrong on a return, which dropped an admin who had
 * switched to Teacher back onto Admin every time they came out of a lesson plan or a record.
 *
 * Held in memory rather than in storage: a fresh page load should land on the role default again,
 * and this never needs to outlive the session.
 */
@Injectable({ providedIn: 'root' })
export class TeacherToolsViewModeService {
  private chosen: TeacherToolsViewMode | null = null;

  /**
   * The tab each surface was last on, keyed by surface.
   *
   * Opening a lesson plan or a record destroys the whole overview, so coming back rebuilt it on
   * its first tab and dropped a reviewer working through Records of work back onto Schemes.
   */
  private tabs = new Map<string, string>();

  rememberTab(surface: string, tab: string): void {
    this.tabs.set(surface, tab);
  }

  tabFor<T extends string>(surface: string, fallback: T): T {
    return (this.tabs.get(surface) as T) ?? fallback;
  }

  /** A surface's picked term or learning area, so switching views and back keeps it. */
  private selections = new Map<string, number>();

  rememberSelection(key: string, id: number | null): void {
    if (id === null) {
      this.selections.delete(key);
    } else {
      this.selections.set(key, id);
    }
  }

  /** The remembered id when it is still among `available`, otherwise the first of them. */
  selectionFor(key: string, available: { id: number }[]): number | null {
    const remembered = this.selections.get(key);
    if (remembered !== undefined && available.some((option) => option.id === remembered)) {
      return remembered;
    }
    return available[0]?.id ?? null;
  }

  /**
   * The surface in use, or null when the user has not switched on this visit.
   *
   * A screen reached by its own route cannot see which surface it was opened from, so it asks
   * here — which is what decides whether a record is being written up or reviewed.
   */
  get current(): TeacherToolsViewMode | null {
    return this.chosen;
  }

  remember(view: TeacherToolsViewMode): void {
    this.chosen = view;
  }

  /** The surface on screen, chosen or defaulted — what the header names as the role in use. */
  private shown: TeacherToolsViewMode | null = null;

  get inUse(): TeacherToolsViewMode | null {
    return this.shown ?? this.chosen;
  }

  showing(view: TeacherToolsViewMode): void {
    this.shown = view;
  }

  /**
   * The view to show: what the user last chose, or the landing default of Admin > HOD > Teacher.
   *
   * A remembered view the user no longer holds falls back to the default, so a revoked role
   * cannot strand them on a surface they can no longer use.
   */
  resolve(isAdmin: boolean, isHod: boolean): TeacherToolsViewMode {
    const fallback: TeacherToolsViewMode = isAdmin ? 'ADMIN' : isHod ? 'HOD' : 'TEACHER';
    if (this.chosen === null) {
      return fallback;
    }
    if (this.chosen === 'ADMIN' && !isAdmin) {
      return fallback;
    }
    if (this.chosen === 'HOD' && !isHod) {
      return fallback;
    }
    return this.chosen;
  }
}
